import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ResultTestFiltersService } from "../services/result-test-filters.service";

@Component({
  selector: "app-magic-link",
  templateUrl: "./magic-link.component.html",
  styleUrls: ["./magic-link.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MagicLinkComponent implements OnInit, OnDestroy {
  @Input() evaluationId!: string | null;
  @Input() baseUrl?: string | null = null;
  @Input() secret: string = "simple-secret-change-me";

  private destroy$ = new Subject<void>();

  resultFilters: any = { facilityIds: [], entities: [] };

  isGenerating = false;
  generatedUrl: string | null = null;
  error: string | null = null;
  copied = false;

  private regenTimer: any = null;
  private readonly REGEN_DEBOUNCE_MS = 500;

  constructor(
    public resultTestFilterService: ResultTestFiltersService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.resultTestFilterService && this.resultTestFilterService.filters$) {
      this.resultTestFilterService.filters$
        .pipe(takeUntil(this.destroy$))
        .subscribe((f) => {
          this.resultFilters = f || { facilityIds: [], entities: [] };
          this.cdr.markForCheck();

          if (this.generatedUrl) {
            if (this.regenTimer) {
              clearTimeout(this.regenTimer);
            }
            this.regenTimer = setTimeout(() => {
              if (!this.isGenerating) {
                this.generate();
              } else {
                setTimeout(() => {
                  if (!this.isGenerating) {
                    this.generate();
                  }
                }, 300);
              }
            }, this.REGEN_DEBOUNCE_MS);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.regenTimer) {
      clearTimeout(this.regenTimer);
      this.regenTimer = null;
    }
  }

  async generate() {
    this.error = null;
    if (!this.evaluationId) {
      this.error = "evaluationId is required";
      this.cdr.markForCheck();
      return;
    }

    this.zone.run(() => {
      this.isGenerating = true;
      this.cdr.markForCheck();
    });

    try {
      const origin = this.baseUrl || window.location.origin;
      const resultPath = `/result/${this.evaluationId}`;
      const iss = `${origin}${resultPath}`;

      const payload: any = {
        evaluationId: this.evaluationId,
        filters: this.resultFilters || {},
        iss,
        exp: Math.floor(Date.now() / 1000) + 72 * 3600,
      };

      const token = await this.createJwt(this.secret, payload);
      const finalUrl = `${iss}?magicLinkToken=${encodeURIComponent(token)}`;

      // Обновляем состояние внутри зоны
      this.zone.run(() => {
        this.generatedUrl = finalUrl;
        this.isGenerating = false;
        this.cdr.markForCheck();
      });

      console.log("Magic link generated:", finalUrl);
    } catch (e: any) {
      console.error("Magic link generation error", e);
      this.zone.run(() => {
        this.error = (e && e.message) || String(e);
        this.isGenerating = false;
        this.cdr.markForCheck();
      });
    } finally {
      this.zone.run(() => {
        this.isGenerating = false;
        this.cdr.markForCheck();
      });
      console.log("generate() finished");
    }
  }

  async copyToClipboard() {
    if (!this.generatedUrl) return;
    try {
      await navigator.clipboard.writeText(this.generatedUrl);
      this.zone.run(() => {
        this.copied = true;
        this.cdr.markForCheck();
      });
      setTimeout(() => {
        this.zone.run(() => {
          this.copied = false;
          this.cdr.markForCheck();
        });
      }, 2000);
    } catch (e) {
      console.error("Copy failed", e);
      this.zone.run(() => {
        this.error = "Копирование не удалось";
        this.cdr.markForCheck();
      });
    }
  }

  // --- JWT HS256 (Web Crypto) ---
  private async createJwt(secret: string, payloadObj: any): Promise<string> {
    const header = { alg: "HS256", typ: "JWT" };
    const encoder = new TextEncoder();

    const base64UrlEncode = (input: Uint8Array | string) => {
      let str: string;
      if (typeof input === "string") {
        str = btoa(unescape(encodeURIComponent(input)));
      } else {
        let s = "";
        for (let i = 0; i < input.length; i++)
          s += String.fromCharCode(input[i]);
        str = btoa(s);
      }
      return str.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    };

    const headerStr = JSON.stringify(header);
    const payloadStr = JSON.stringify(payloadObj);

    const encodedHeader = base64UrlEncode(headerStr);
    const encodedPayload = base64UrlEncode(payloadStr);
    const data = `${encodedHeader}.${encodedPayload}`;

    const key = await crypto.subtle.importKey(
      "raw",
      encoder.encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );

    const signatureBuf = await crypto.subtle.sign(
      "HMAC",
      key,
      encoder.encode(data),
    );
    const signatureArray = new Uint8Array(signatureBuf);
    const signature = base64UrlEncode(signatureArray);

    return `${data}.${signature}`;
  }
}
